import { createContext, useCallback, useContext, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { toastSpec, type ToastTone } from "../specs/display";
import { mergeVars, typographyClass } from "../specs/types";
import { CloseIcon } from "./Icons";

interface ToastItem {
  id: number;
  title: string;
  subtitle?: string;
  tone: ToastTone;
}

interface ToastApi {
  show: (toast: { title: string; subtitle?: string; tone?: ToastTone }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Время показа зависит от объёма текста — правило из спецификации компонента. */
function autoHideDelay(title: string, subtitle?: string): number {
  const { titleLimit, subtitleLimit, short, long } = toastSpec.timing;
  const isShort = title.length <= titleLimit && (subtitle?.length ?? 0) <= subtitleLimit;
  return isShort ? short : long;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const show = useCallback<ToastApi["show"]>(
    ({ title, subtitle, tone = toastSpec.defaults.tone }) => {
      const id = Date.now() + Math.random();
      setItems((current) => [...current, { id, title, subtitle, tone }]);
      window.setTimeout(() => dismiss(id), autoHideDelay(title, subtitle));
    },
    [dismiss],
  );

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ui-toast-viewport" style={toastSpec.base as CSSProperties}>
        {items.map((item) => (
          <div key={item.id} className="ui-toast" style={mergeVars(toastSpec.base, toastSpec.tones[item.tone]) as CSSProperties}>
            <span className="ui-toast__marker" />
            <div className="ui-toast__body">
              <span className={typographyClass(toastSpec.titleTypography)}>{item.title}</span>
              {item.subtitle && (
                <span className={`ui-toast__subtitle ${typographyClass(toastSpec.subtitleTypography)}`}>
                  {item.subtitle}
                </span>
              )}
            </div>
            <button type="button" className="ui-toast__close" onClick={() => dismiss(item.id)} aria-label="close">
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside <ToastProvider>");
  return context;
}
